import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, AlertTriangle, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import OnboardingShell from '../components/OnboardingShell';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Lands here after Stripe redirects back from onboarding. Polls
 * /api/restaurants/{id}/stripe/status (which Stripe-side auto-syncs the
 * charges_enabled flag in the DB) up to 6 times, then routes:
 *   - charges_enabled === true → /onboarding/setup (Step 4 — Go live)
 *   - otherwise → /onboarding/stripe-connect (with hint)
 */
const OnboardingStripeReturnPage = () => {
  const navigate = useNavigate();
  const { token, restaurant, fetchMe } = useAuth();
  const [state, setState] = useState('verifying'); // verifying | success | incomplete

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }
    let attempts = 0;
    let cancelled = false;

    const pollOnce = async () => {
      attempts += 1;
      try {
        const rid = restaurant?.id;
        if (!rid) {
          // Hydrate via onboarding state if AuthContext didn't carry restaurant
          const s = await axios.get(`${API}/api/onboarding/state`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (s.data?.checklist?.stripe_connected) {
            // iter141 — Stripe Connect complete. The next step in the
            // new financial-first sequence is the 45-day free trial
            // activation, NOT the setup page. Advance the cursor to
            // 'subscribe' and navigate the browser to /onboarding/trial.
            await axios.post(
              `${API}/api/onboarding/advance`,
              { step: 'subscribe' },
              { headers: { Authorization: `Bearer ${token}` } },
            );
            if (!cancelled) {
              setState('success');
              setTimeout(() => navigate('/onboarding/trial'), 700);
            }
            return;
          }
        } else {
          const r = await axios.get(`${API}/api/restaurants/${rid}/stripe/status`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (r.data?.charges_enabled || r.data?.details_submitted || r.data?.onboarding_complete) {
            // iter141 — see comment above. Trial page is Step 4 now.
            await axios.post(
              `${API}/api/onboarding/advance`,
              { step: 'subscribe' },
              { headers: { Authorization: `Bearer ${token}` } },
            );
            await fetchMe?.();
            if (!cancelled) {
              setState('success');
              setTimeout(() => navigate('/onboarding/trial'), 700);
            }
            return;
          }
        }
      } catch {
        /* swallow; we'll retry */
      }
      if (attempts >= 12 && !cancelled) {
        setState('incomplete');
        return;
      }
      setTimeout(pollOnce, 2000);
    };

    pollOnce();
    return () => {
      cancelled = true;
    };
  }, [token, restaurant?.id, navigate, fetchMe]);

  return (
    <OnboardingShell
      currentStep="stripe_connect"
      backFallback="/onboarding/stripe-connect"
      showBack={state !== 'verifying'}
      testid="onboarding-stripe-return-page"
    >
        <div className="mt-2 max-w-md mx-auto text-center">
          {state === 'verifying' && (
            <div data-testid="stripe-return-verifying">
              <div className="mx-auto w-16 h-16 rounded-2xl bg-yellow-500/[0.08] border border-yellow-500/30 flex items-center justify-center mb-6">
                <Loader2 className="w-7 h-7 text-yellow-400 animate-spin" />
              </div>
              <h2 className="font-['Outfit',_Inter,_sans-serif] text-2xl font-semibold tracking-tight text-white">
                Verifying your Stripe setup…
              </h2>
              <p className="mt-2 text-[13.5px] text-white/55 leading-relaxed">
                Confirming that Stripe enabled payouts on your account. This takes about 5 seconds.
              </p>
            </div>
          )}

          {state === 'success' && (
            <div data-testid="stripe-return-success">
              <div className="mx-auto w-16 h-16 rounded-2xl bg-emerald-500/[0.10] border border-emerald-500/40 flex items-center justify-center mb-6">
                <CheckCircle2 className="w-8 h-8 text-emerald-300" strokeWidth={1.7} />
              </div>
              <h2 className="font-['Outfit',_Inter,_sans-serif] text-2xl font-semibold tracking-tight text-white">
                Stripe connected successfully
              </h2>
              <p className="mt-2 text-[13.5px] text-white/65 leading-relaxed">
                Your restaurant is ready to receive payouts. Taking you to setup…
              </p>
            </div>
          )}

          {state === 'incomplete' && (
            <div data-testid="stripe-return-incomplete">
              <div className="mx-auto w-16 h-16 rounded-2xl bg-yellow-500/[0.08] border border-yellow-500/40 flex items-center justify-center mb-6">
                <AlertTriangle className="w-7 h-7 text-yellow-300" strokeWidth={1.7} />
              </div>
              <h2 className="font-['Outfit',_Inter,_sans-serif] text-2xl font-semibold tracking-tight text-white">
                Almost there
              </h2>
              <p className="mt-2 text-[13.5px] text-white/65 leading-relaxed">
                Stripe still needs a few details before payouts can be enabled. Pick up where you
                left off — your progress is saved.
              </p>
              <button
                type="button"
                onClick={() => navigate('/onboarding/stripe-connect')}
                data-testid="stripe-return-continue-btn"
                className="mt-7 inline-flex items-center justify-center gap-2 px-6 py-3 min-h-[52px] rounded-2xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-extrabold text-[14px] transition-all shadow-[0_15px_40px_-15px_rgba(250,204,21,0.55)]"
              >
                Resume Stripe setup <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
    </OnboardingShell>
  );
};

export default OnboardingStripeReturnPage;
